import { useEffect, useRef, useState } from 'react';
import type { BridgeEventStream } from '../resources/bridge-events.js';
import type { ShogunChopPushFrameStream } from '../resources/shogunchop-push-frames.js';
import type { EvaluatedFlags } from '../types/flags.js';
import type { LocalAgentSDK } from '../client.js';

// A single-shot change-frame re-evaluate that fails (e.g. the definitions
// reload hanging past the SDK's 30s abort) would leave a killed gate OPEN
// until the NEXT change frame — which may never come. Retry on the same
// endpoint until a successful evaluate converges the answer; every failure
// still keeps the last answer.
const REEVALUATE_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

/**
 * useFlag: pull the evaluated flags on mount and
 * re-evaluate whenever a change frame arrives — `flags.changed` on the web
 * cloud bridge (bridge ADR-003 broadcast lane) or the daemon's `flags.updated`
 * relay on desktop. The client never computes membership — the
 * backend is the only evaluator (flags ADR-002).
 *
 * Returns false until the first evaluate resolves and for an unknown key:
 * an absent key is OFF for the caller (fail closed).
 */
export function useFlag(
  sdk: LocalAgentSDK | null,
  stream: BridgeEventStream | null,
  key: string,
  daemonStream?: ShogunChopPushFrameStream | null
): boolean {
  const [flags, setFlags] = useState<EvaluatedFlags | null>(null);
  // Bounded retry state for a failed change-frame re-evaluate; the timer is
  // cleared on unmount and on the next change frame.
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!sdk) return;
    let cancelled = false;
    const evaluate = () => {
      sdk.flags
        .evaluate()
        .then((next) => {
          if (!cancelled) setFlags(next);
        })
        .catch((cause: unknown) => {
          // Justified degradation (no-fallbacks.md): during a definitions
          // outage open clients keep the last evaluated answer — never flip a
          // gate open on a failed evaluate.
          console.warn('useFlag: evaluate failed', cause);
        });
    };
    evaluate();
    return () => {
      cancelled = true;
    };
  }, [sdk]);

  useEffect(
    () => {
      const clearRetry = (): void => {
        if (retryTimer.current !== null) {
          clearTimeout(retryTimer.current);
          retryTimer.current = null;
        }
      };
      const reevaluate = (attempt: number): void => {
        if (!sdk) return;
        clearRetry();
        sdk.flags
          .evaluate()
          .then(setFlags)
          .catch((cause: unknown) => {
            console.warn('useFlag: re-evaluate failed', cause);
            if (attempt < REEVALUATE_RETRY_DELAYS_MS.length) {
              retryTimer.current = setTimeout(
                () => reevaluate(attempt + 1),
                REEVALUATE_RETRY_DELAYS_MS[attempt]
              );
            }
          });
      };
      const unsubStream = stream?.onFlagsChanged(() => reevaluate(0));
      const unsubDaemon = daemonStream?.onFlagsUpdated(() => reevaluate(0));
      return () => {
        clearRetry();
        unsubStream?.();
        unsubDaemon?.();
      };
    },
    // The two streams are interchangeable per platform; both subscribe.
    [stream, daemonStream, sdk]
  );

  return flags?.[key] === true;
}
